'use strict';

import Vue from 'vue';
import axios from 'axios';
import moment from 'moment';
import {
    removeAdrielJwt,
    getAdrielJwtRefreshToken,
    getAdrielJwtRefreshTokenExpireTimeMilliseconds,
    setAdrielTokens,
    isValueExist,
} from '@/utils/utils';
import { AuthService } from '@/services';
import EventBus from '@/plugins/eventBus';

let config = {
    baseURL: process.env.VUE_APP_API_URL || '/api',
    withCredentials: true,
};

const _axios = axios.create(config);

_axios.setAccountId = (id) => {
    _axios.defaults.headers.common.AccountId = id;
};

_axios.interceptors.response.use(function (response) {
    return response;
}, errorResponseCallback);

async function errorResponseCallback(error) {
    if (!error.config || !isTokenExpiredError(error))
        return Promise.reject(error);

    // TODO: handle concurrency
    const refreshTokenExpMs = Number(
        getAdrielJwtRefreshTokenExpireTimeMilliseconds()
    );

    if (refreshTokenExpMs && !isTokenExpired(refreshTokenExpMs)) {
        return attemptRefreshTokenOrLogout(error);
    }

    return handleAuthFailure(error);
}

/**
 * `STALE_USER_SESSION` no longer reaches us: the server stopped throwing it on
 * 2026-09-18 (`adriel_server` 3ab72124ea) and now treats a stale passport
 * session as unauthenticated so a fresh login can proceed. The branch stays as
 * a guard for an older server during a rollback or a staged deploy; refresh
 * fails the same way for it, so it ends in `handleAuthFailure` either way.
 */
function isTokenExpiredError(error) {
    const code = error.response?.data?.code;
    const status = error.response?.status;
    return (
        status === 401 &&
        (code === 'INVALID_TOKEN' || code === 'STALE_USER_SESSION')
    );
}

function isTokenExpired(expInMilliseconds) {
    return moment().isSameOrAfter(expInMilliseconds);
}

async function attemptRefreshTokenOrLogout(error) {
    removeAdrielJwt();

    try {
        const tokens = await AuthService.refreshAccessToken({
            refreshToken: getAdrielJwtRefreshToken(),
        });

        if (!isValueExist(tokens) || !tokens.token) throw new Error('No token');

        setAdrielTokens({
            ...tokens,
        });

        AuthService.setAccessToken$.next(tokens.token);
        error.config.headers.Authorization = `Bearer ${tokens.token}`;
        return tokens.token ? _axios(error.config) : handleAuthFailure(error);
    } catch (e) {
        console.error(e);
        // 400/401 means the server refused the refresh token itself
        // (two-factor changed, TASK-14321), not a network error or a 5xx.
        const refreshRejected = [400, 401].includes(e?.response?.status);
        return handleAuthFailure(error, { refreshRejected });
    }
}

async function handleAuthFailure(error, detail = {}) {
    EventBus.$emit('auth-failed', detail);
    return Promise.reject(error);
}

Plugin.install = function (Vue) {
    Vue.axios = _axios;
    window.axios = _axios;
    Object.defineProperties(Vue.prototype, {
        axios: {
            get() {
                return _axios;
            },
        },
        $axios: {
            get() {
                return _axios;
            },
        },
    });
};

Vue.use(Plugin);

export default Plugin;
